import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Language, Translations } from './types';
import { nl as nlTranslations } from './nl';
import { en as enTranslations } from './en';
import { supabase } from '@/integrations/supabase/client';
import { getGameFormatForms, type GameFormatKey } from '@/hooks/use-game-format';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof Translations, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'preferred_language';

const defaultTranslations: Record<Language, Translations> = {
  nl: nlTranslations,
  en: enTranslations,
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('nl');

  // Read the stored preference after hydration (localStorage is browser-only).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'en') setLanguageState('en');
    } catch {
      // ignore
    }
  }, []);


  const [overrides, setOverrides] = useState<Partial<Record<string, string>>>({});
  const [gameFormatKey, setGameFormatKey] = useState<GameFormatKey>('hussel');

  // Load overrides from database (for super-admin custom translations)
  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const { data, error } = await supabase
          .from('translations' as any)
          .select('key, en')
          .order('key');
        
        if (!error && data) {
          const overrideMap: Record<string, string> = {};
          (data as any[]).forEach((row: any) => {
            if (row.en) overrideMap[row.key] = row.en;
          });
          setOverrides(overrideMap);
        }
      } catch {
        // Table might not exist yet, that's fine
      }
    };
    loadOverrides();
  }, []);

  // Load game format from user's club
  useEffect(() => {
    const loadGameFormat = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('club_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!profile?.club_id) return;

        const { data: club } = await supabase
          .from('clubs_public')
          .select('game_format_name')
          .eq('id', profile.club_id)
          .maybeSingle();

        if (club?.game_format_name) {
          setGameFormatKey(club.game_format_name as GameFormatKey);
        }
      } catch {
        // Ignore errors
      }
    };
    loadGameFormat();

    // Re-check on auth change
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadGameFormat();
    });
    return () => subscription.unsubscribe();
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  const t = useCallback((key: keyof Translations, params?: Record<string, string | number>): string => {
    let value: string;
    
    if (language === 'en' && overrides[key]) {
      value = overrides[key]!;
    } else {
      value = defaultTranslations[language][key] || defaultTranslations['nl'][key] || String(key);
    }

    // Auto-inject game format params
    const format = getGameFormatForms(gameFormatKey);
    const allParams: Record<string, string | number> = {
      gameFormatVerb: format.verb,
      gameFormatNoun: format.noun,
      gameFormatNounCapital: format.nounCapital,
      gameFormatVerbCapital: format.verbCapital,
      gameFormatEvent: format.eventName,
      gameFormatEventCapital: format.eventNameCapital,
      gameFormatSession: format.sessionName,
      ...params,
    };

    // Replace {param} placeholders
    Object.entries(allParams).forEach(([paramKey, paramValue]) => {
      value = value.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
    });

    return value;
  }, [language, overrides, gameFormatKey]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};
